<template>
  <div class="quest-location-field">
    <div class="location-summary">
      <span class="location-summary__pin" aria-hidden="true">📍</span>
      <div>
        <strong>{{ activePointNumber ? `Emplacement du point ${activePointNumber}` : hasLocation ? 'Lieu du rendez-vous choisi' : 'Où les aventuriers doivent-ils venir ?' }}</strong>
        <p>{{ coordinatesLabel || 'Ouvrez la carte, placez le repère, puis confirmez.' }}</p>
      </div>
    </div>
    <button ref="trigger" type="button" class="location-button" @click="openPicker">
      {{ hasLocation ? 'Modifier le lieu' : 'Choisir sur la carte' }}
    </button>

    <!-- Le top layer natif échappe au clipping du formulaire sans agrandir
         Leaflet par-dessus les commandes. Échap ne ferme que ce dialogue. -->
    <dialog ref="dialog" class="location-dialog" :aria-labelledby="titleId"
      @cancel.prevent="cancel" @close="onClose" @keydown.esc.stop.prevent="cancel" @keydown.tab.stop>
      <template v-if="opened">
        <header class="location-dialog__header">
          <div><span>{{ activePointNumber ? 'Carte du parcours' : 'Lieu du mandat' }}</span><h2 :id="titleId">{{ activePointNumber ? `Placer le point ${activePointNumber}` : 'Placer le rendez-vous' }}</h2></div>
          <button type="button" class="location-button" autofocus @click="cancel">Annuler</button>
        </header>
        <p v-if="activePointNumber" class="location-dialog__legend"><strong>Point {{ activePointNumber }} : repère vert cerclé.</strong> {{ journeyOrder === 'ordered' ? 'Les numéros indiquent l’ordre du parcours.' : 'Les numéros repèrent les étapes ; le joueur choisit son ordre.' }}</p>
        <MapPicker v-model="draft" class="location-dialog__map" street-map emoji="📍" :journey-points="journeyPoints" :active-point-id="activePointId" />
        <footer class="location-dialog__footer">
          <p>{{ validDraft ? 'Le repère sera enregistré dans votre brouillon.' : 'Touchez la carte pour placer le repère.' }}</p>
          <button type="button" class="location-button location-button--confirm" :disabled="!validDraft" @click="confirm">
            Utiliser ce lieu
          </button>
        </footer>
      </template>
    </dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import MapPicker from './MapPicker.vue';

type Coordinates = { lat: number | null; lng: number | null };
const props = withDefaults(defineProps<{ modelValue: Coordinates | null; reviewOpen?: boolean; journeyPoints?: Array<{id:string;lat:number;lng:number;title:string}>; activePointId?: string; journeyOrder?: string }>(), { reviewOpen: false, journeyPoints: () => [], activePointId: '', journeyOrder: 'free' });
const activePointNumber = computed(() => props.journeyPoints.findIndex(point => point.id === props.activePointId) + 1);
const titleId = `quest-location-${crypto.randomUUID()}`;
const emit = defineEmits<{ 'update:modelValue': [value: Coordinates] }>();
const trigger = ref<HTMLButtonElement | null>(null);
const dialog = ref<HTMLDialogElement | null>(null);
const opened = ref(false);
const draft = ref<Coordinates | null>(null);
const hasLocation = computed(() => props.modelValue?.lat != null && props.modelValue?.lng != null);
const coordinatesLabel = computed(() => props.modelValue?.lat != null && props.modelValue?.lng != null
  ? `${props.modelValue.lat.toFixed(5)}, ${props.modelValue.lng.toFixed(5)}` : '');
const validDraft = computed(() => draft.value?.lat != null && draft.value?.lng != null);

async function openPicker() {
  draft.value = hasLocation.value ? { ...props.modelValue! } : null;
  opened.value = true;
  await nextTick();
  dialog.value?.showModal();
}
function cancel() { dialog.value?.close(); }
function confirm() {
  if (!validDraft.value || !draft.value) return;
  emit('update:modelValue', { ...draft.value });
  dialog.value?.close();
}
function onClose() {
  opened.value = false;
  trigger.value?.focus({ preventScroll: true });
}
onMounted(() => { if (import.meta.env.DEV && props.reviewOpen) void openPicker(); });
onBeforeUnmount(() => dialog.value?.close());
</script>

<style scoped>
.quest-location-field { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 14px; padding: 16px; border: 1px solid #9a7442; border-radius: 10px; background: #fff7e0; }
.location-summary { display: flex; align-items: center; gap: 12px; min-width: 0; flex: 1 1 240px; color: #3d2b1b; }
.location-summary__pin { font-size: 26px; }
.location-summary strong { font-size: 14px; }
.location-summary p { margin: 5px 0 0; color: #765f45; font-size: 13px; line-height: 1.5; }
.location-button { min-height: 44px; padding: 10px 16px; border: 1px solid #8c6738; border-radius: 7px; background: #f7ebce; color: #56391d; font: 700 14px/1.3 var(--font-body, sans-serif); cursor: pointer; }
.location-button:focus-visible { outline: 3px solid #8b6536; outline-offset: 2px; }
.location-button--confirm { color: #fff3d8; border-color: #5b381b; background: #654323; }
.location-button:disabled { opacity: .5; cursor: not-allowed; }
.location-dialog { position: fixed; inset: 0; box-sizing: border-box; width: min(1100px, calc(100vw - 32px)); height: min(740px, calc(100dvh - 32px)); max-width: none; max-height: none; margin: auto; padding: 0; overflow: hidden; border: 2px solid #8b6637; border-radius: 12px; color: #3d2b1b; background: #ecdbb6; box-shadow: 0 24px 70px #0009; }
.location-dialog[open] { display: grid; grid-template-rows: auto minmax(0, 1fr) auto; }
.location-dialog:has(.location-dialog__legend)[open] { grid-template-rows: auto auto minmax(0, 1fr) auto; }
.location-dialog__legend { margin: 0; padding: 8px 18px 0; font-size: 13px; line-height: 1.4; color: #56391d; }
.location-dialog__legend strong { color: #205853; }
.location-dialog::backdrop { background: #13100dd9; }
.location-dialog__header, .location-dialog__footer { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 18px; }
.location-dialog__header { border-bottom: 1px solid #af8f59; }
.location-dialog__header span { font-size: 11px; text-transform: uppercase; letter-spacing: .12em; color: #765f45; }
.location-dialog__header h2 { margin: 3px 0 0; font: 700 20px/1.25 var(--font-title, Georgia, serif); }
.location-dialog__header .location-button { flex-shrink: 0; }
.location-dialog__footer { border-top: 1px solid #af8f59; }
.location-dialog__footer p { margin: 0; font-size: 13px; line-height: 1.4; }
.location-dialog__footer .location-button { flex-shrink: 0; }
.location-dialog__map { min-height: 0; padding: 12px 18px 8px; overflow: hidden; }
.location-dialog__map :deep(.map-container) { height: auto; flex: 1 1 0; min-height: 0; isolation: isolate; }
.location-dialog__map :deep(.map-search), .location-dialog__map :deep(.map-hint), .location-dialog__map :deep(.map-error) { flex-shrink: 0; }
.location-dialog__map :deep(.map-search-input) { min-height: 44px; font-size: 16px; color: #3d2b1b; background: #fff7e0; border-color: #9a7442; }
.location-dialog__map :deep(.map-search .btn) { min-width: 44px; min-height: 44px; color: #56391d; background: #f7ebce; border: 1px solid #8c6738; box-shadow: none; }
.location-dialog__map :deep(.map-hint) { color: #765f45; font-size: 12px; }
@media (max-width: 640px), (max-height: 600px) and (max-width: 1100px) {
  .location-dialog { width: 100%; height: 100dvh; border: 0; border-radius: 0; }
  .location-dialog__header { padding: max(8px, env(safe-area-inset-top)) max(12px, env(safe-area-inset-right)) 8px max(12px, env(safe-area-inset-left)); }
  .location-dialog__footer { padding: 8px max(12px, env(safe-area-inset-right)) max(8px, env(safe-area-inset-bottom)) max(12px, env(safe-area-inset-left)); }
  .location-dialog__map { padding: 8px max(12px, env(safe-area-inset-right)) 6px max(12px, env(safe-area-inset-left)); }
}
@media (max-width: 480px) {
  .quest-location-field > .location-button { width: 100%; }
  .location-dialog__footer { align-items: stretch; flex-direction: column; gap: 6px; }
  .location-dialog__header h2 { font-size: 18px; }
}
</style>
